/**
 * ScoreBird Properties Tab — shows live NeST data in the Properties panel.
 * Renders as a second tab alongside "Properties" when a ScoreBird connection is active.
 *
 * Two views:
 *   1. Settings — Connection Name, NeST ID, API Key, "Connect to API Server" button
 *   2. Live Local Data — table: Lock | NeST Variable | Received Value | → | Our Variable | Our Value
 *      with yellow highlight flash on rows receiving new data.
 */
import { getConnection, connectScorebird, disconnectScorebird, DEMO_NEST_ID } from '../engine/scorebirdPoller';
import { getPendingSport, clearPendingSport } from './dataControllerMenu';
import { dataStore } from '../stores/dataStore';
import { eventBus } from '../engine/eventBus';

const NEST_ID_KEY = 'graphicsim:scorebird:nestId';
const API_KEY_DISPLAY = 'graphicsim:scorebird:apiKey';
const CONN_NAME_KEY = 'graphicsim:scorebird:connName';

// Track which fields just changed for yellow highlight
let changedFields = new Set<string>();
let highlightTimer: ReturnType<typeof setTimeout> | null = null;

// Track previous values to detect changes
let prevValues: Record<string, string> = {};

/** Listen for data row changes to track highlights */
export function initScorebirdPanel(): void {
  eventBus.on('data:rowChanged', ({ sourceId }) => {
    const source = dataStore.getState().sources.find((s) => s.id === sourceId);
    if (!source || (source.type as string) !== 'scorebird') return;

    const row = source.rows[0] || {};
    const newChanged = new Set<string>();

    for (const [key, value] of Object.entries(row)) {
      if (prevValues[key] !== value) {
        newChanged.add(key);
      }
    }

    prevValues = { ...row };

    if (newChanged.size > 0) {
      changedFields = newChanged;

      // Clear highlight after 1.5s
      if (highlightTimer) clearTimeout(highlightTimer);
      highlightTimer = setTimeout(() => {
        changedFields.clear();
        // Re-render the live data table rows to remove highlights
        updateHighlights();
      }, 1500);

      // Apply highlights immediately
      updateHighlights();
    }
  });
}

/** Update yellow highlights on existing rows without full re-render */
function updateHighlights(): void {
  const rows = document.querySelectorAll<HTMLElement>('.sb-data-row');
  rows.forEach((row) => {
    const field = row.dataset.field;
    if (field && changedFields.has(field)) {
      row.classList.add('sb-highlight');
    } else {
      row.classList.remove('sb-highlight');
    }
  });
}

/** Check if this project item has a ScoreBird connection */
export function hasScoreBirdConnection(projectItemId: string): boolean {
  return getConnection(projectItemId) !== null;
}

/**
 * Render the ScoreBird tab content for the Properties panel.
 * Returns HTML string + attaches handlers after insertion.
 */
export function renderScorebirdTab(projectItemId: string, container: HTMLElement): void {
  const conn = getConnection(projectItemId);
  const showSettings = !conn;

  if (showSettings) {
    renderSettingsView(projectItemId, container);
  } else {
    renderLiveDataView(projectItemId, conn, container);
  }
}

function renderSettingsView(projectItemId: string, container: HTMLElement): void {
  const savedNestId = localStorage.getItem(NEST_ID_KEY) || '';
  const savedApiKey = localStorage.getItem(API_KEY_DISPLAY) || '';
  const savedConnName = localStorage.getItem(CONN_NAME_KEY) || '';

  container.innerHTML = `
    <div class="sb-panel">
      <div class="sb-header-label">Scorebird: New Connection</div>
      <div class="sb-subtitle">Configure device connection.</div>

      <h3 class="sb-section-title">Settings</h3>

      <div class="sb-settings-box">
        <h4 class="sb-box-title">Recent Scorebird Connection Settings</h4>
        <div class="sb-recent-row">
          <button class="sb-btn sb-add-new">Add New</button>
        </div>

        <div class="sb-form">
          <label class="sb-dropdown-label">Connect to Scorebird Server</label>
          <select class="sb-select sb-connect-mode">
            <option value="cloud">Connect to Scorebird Server</option>
          </select>
        </div>

        <div class="sb-form">
          <div class="sb-form-row">
            <label class="sb-form-label">Connection Name</label>
            <input type="text" class="sb-form-input sb-conn-name" value="${escAttr(savedConnName)}" placeholder="My Connection" />
          </div>
          <div class="sb-form-hint">Give this connection a name to identify it in the list of recent connections.</div>
        </div>

        <div class="sb-form">
          <div class="sb-form-row">
            <label class="sb-form-label">NeST ID (Serial Number)</label>
            <input type="text" class="sb-form-input sb-nest-id" value="${escAttr(savedNestId || DEMO_NEST_ID)}" placeholder="${DEMO_NEST_ID}" />
          </div>
          <div class="sb-form-hint">Waiting on your ScoreBird? Leave demo unit ${DEMO_NEST_ID} in place — it is a real device, so you will see live football data whenever a game is running on it.</div>
        </div>

        <div class="sb-form">
          <div class="sb-form-row">
            <label class="sb-form-label">API Key (from Scorebird)</label>
            <div class="sb-api-key-display">Configured on server</div>
          </div>
        </div>

        <div class="sb-form-actions">
          <button class="sb-btn sb-connect-btn">Connect to API Server</button>
        </div>
      </div>

      <div class="sb-footer">
        <button class="sb-btn sb-open-browser">Open in Browser</button>
      </div>
    </div>
  `;

  // Attach handlers
  container.querySelector('.sb-connect-btn')?.addEventListener('click', () => {
    const nestId = container.querySelector<HTMLInputElement>('.sb-nest-id')?.value.trim();
    const apiKey = container.querySelector<HTMLInputElement>('.sb-api-key')?.value.trim();
    const connName = container.querySelector<HTMLInputElement>('.sb-conn-name')?.value.trim();

    if (!nestId) {
      alert('Enter a NeST ID');
      return;
    }

    // Save for next time
    localStorage.setItem(NEST_ID_KEY, nestId);
    if (apiKey) localStorage.setItem(API_KEY_DISPLAY, apiKey);
    if (connName) localStorage.setItem(CONN_NAME_KEY, connName);

    // Use the pending sport from Data Controller menu, or default to football
    const conn = getConnection(projectItemId);
    const sport = conn?.sport || getPendingSport(projectItemId) || 'football';
    connectScorebird(projectItemId, nestId, sport);
    clearPendingSport(projectItemId);
    renderLiveDataView(projectItemId, getConnection(projectItemId)!, container);
  });

  container.querySelector('.sb-open-browser')?.addEventListener('click', () => {
    const nestId = container.querySelector<HTMLInputElement>('.sb-nest-id')?.value.trim() || savedNestId;
    if (nestId) {
      window.open(`https://widget.scorebird.com/broadcast-overlay.html?serial=${nestId}&sport=football`, '_blank');
    }
  });
}

function renderLiveDataView(projectItemId: string, conn: { nestId: string; sport: string }, container: HTMLElement): void {
  const source = dataStore.getState().sources.find((s) =>
    (s.type as string) === 'scorebird' && s.name.includes(conn.nestId)
  );

  const row = source?.rows[0] || {};
  const links = source ? dataStore.getState().links.filter((l) => l.dataSourceId === source.id) : [];

  // Build field mapping: normalized key → linked template field
  const fieldMap = new Map<string, { ourVariable: string; ourValue: string }>();
  for (const link of links) {
    const value = row[link.columnName] || '';
    fieldMap.set(link.columnName, { ourVariable: link.fieldId, ourValue: value });
  }

  // Build NeST variable → received value list from FIELD_MAP (reverse lookup)
  const nestVariableNames = getNestVariableNames(conn.sport);

  const tableRows = Object.entries(row).map(([normalizedKey, receivedValue]) => {
    const nestVarName = nestVariableNames.get(normalizedKey) || normalizedKey;
    const mapping = fieldMap.get(normalizedKey);
    const isHighlighted = changedFields.has(normalizedKey);

    return `<tr class="sb-data-row${isHighlighted ? ' sb-highlight' : ''}" data-field="${normalizedKey}">
      <td class="sb-lock"><input type="checkbox" class="sb-lock-cb" /></td>
      <td class="sb-nest-var">${escHtml(nestVarName)}</td>
      <td class="sb-received">${escHtml(receivedValue)}</td>
      <td class="sb-arrow">&rarr;</td>
      <td class="sb-our-var">${mapping ? escHtml(formatFieldLabel(mapping.ourVariable)) : ''}</td>
      <td class="sb-our-val">${mapping ? escHtml(mapping.ourValue) : ''}</td>
    </tr>`;
  }).join('');

  container.innerHTML = `
    <div class="sb-panel">
      <div class="sb-header-label">Scorebird: ${capitalize(conn.sport)}.Instance 1</div>
      <div class="sb-subtitle">Displaying device updates.</div>

      <div class="sb-live-header">
        <h3 class="sb-section-title">Live Local Data</h3>
        <span class="sb-status sb-connected">Connected</span>
      </div>

      <div class="sb-data-table-wrap">
        <table class="sb-data-table">
          <thead>
            <tr>
              <th class="sb-th-lock">Lock</th>
              <th class="sb-th-nest">NeST Variable</th>
              <th class="sb-th-recv">Received Value</th>
              <th class="sb-th-arrow"></th>
              <th class="sb-th-our">Our Variable</th>
              <th class="sb-th-val">Our Value</th>
            </tr>
          </thead>
          <tbody class="sb-data-body">
            ${tableRows || '<tr><td colspan="6" class="sb-empty">Waiting for data...</td></tr>'}
          </tbody>
        </table>
      </div>

      <div class="sb-live-footer">
        <button class="sb-btn sb-show-settings">Show Settings</button>
      </div>

      <div class="sb-footer">
        <button class="sb-btn sb-open-browser">Open in Browser</button>
      </div>
    </div>
  `;

  // Show Settings button
  container.querySelector('.sb-show-settings')?.addEventListener('click', () => {
    renderSettingsView(projectItemId, container);
  });

  // Open in Browser
  container.querySelector('.sb-open-browser')?.addEventListener('click', () => {
    window.open(`https://widget.scorebird.com/broadcast-overlay.html?serial=${conn.nestId}&sport=${conn.sport}`, '_blank');
  });

  // Auto-refresh the table when data changes
  const unsub = eventBus.on('data:rowChanged', ({ sourceId }) => {
    if (source && sourceId === source.id && container.isConnected) {
      refreshDataRows(projectItemId, conn, container);
    } else if (!container.isConnected) {
      // Clean up listener when panel is removed from DOM
      unsub?.();
    }
  });
}

/** Refresh just the table body rows without full re-render */
function refreshDataRows(projectItemId: string, conn: { nestId: string; sport: string }, container: HTMLElement): void {
  const tbody = container.querySelector('.sb-data-body');
  if (!tbody) return;

  const source = dataStore.getState().sources.find((s) =>
    (s.type as string) === 'scorebird' && s.name.includes(conn.nestId)
  );
  if (!source) return;

  const row = source.rows[0] || {};
  const links = dataStore.getState().links.filter((l) => l.dataSourceId === source.id);
  const fieldMap = new Map<string, { ourVariable: string; ourValue: string }>();
  for (const link of links) {
    fieldMap.set(link.columnName, { ourVariable: link.fieldId, ourValue: row[link.columnName] || '' });
  }

  const nestVariableNames = getNestVariableNames(conn.sport);

  tbody.innerHTML = Object.entries(row).map(([normalizedKey, receivedValue]) => {
    const nestVarName = nestVariableNames.get(normalizedKey) || normalizedKey;
    const mapping = fieldMap.get(normalizedKey);
    const isHighlighted = changedFields.has(normalizedKey);

    return `<tr class="sb-data-row${isHighlighted ? ' sb-highlight' : ''}" data-field="${normalizedKey}">
      <td class="sb-lock"><input type="checkbox" class="sb-lock-cb" /></td>
      <td class="sb-nest-var">${escHtml(nestVarName)}</td>
      <td class="sb-received">${escHtml(receivedValue)}</td>
      <td class="sb-arrow">&rarr;</td>
      <td class="sb-our-var">${mapping ? escHtml(formatFieldLabel(mapping.ourVariable)) : ''}</td>
      <td class="sb-our-val">${mapping ? escHtml(mapping.ourValue) : ''}</td>
    </tr>`;
  }).join('');
}

// ── Helpers ────────────────────────────────────────────────────

/** Reverse-lookup: normalized field ID → original ScoreBird API key name */
function getNestVariableNames(sport: string): Map<string, string> {
  // Import the field maps inline to avoid circular deps
  const FIELD_MAP: Record<string, Record<string, string>> = {
    common: { 'Home': 'homeScore', 'Away': 'awayScore', 'Time': 'clock', 'Pos': 'possession', 'Secondary Time': 'secondaryTime' },
    football: { 'Home': 'homeScore', 'Away': 'awayScore', 'Time': 'clock', 'Quarter': 'period', 'Down': 'down', 'To Go': 'distance', 'Ball On': 'ballOn', 'Pos': 'possession', 'Home TOL': 'homeTimeouts', 'Away TOL': 'awayTimeouts', 'Flag': 'flag', 'Secondary Time': 'playClock' },
    basketball: { 'Home': 'homeScore', 'Away': 'awayScore', 'Time': 'clock', 'Quarter': 'period', 'Pos': 'possession', 'Home TOL': 'homeTimeouts', 'Away TOL': 'awayTimeouts', 'Home Fouls': 'homeFouls', 'Away Fouls': 'awayFouls', 'Home Bonus': 'homeBonus', 'Home Dbl Bonus': 'homeDblBonus', 'Away Bonus': 'awayBonus', 'Away Dbl Bonus': 'awayDblBonus', 'Secondary Time': 'shotClock' },
  };

  const sportMap = FIELD_MAP[sport] || FIELD_MAP.common;
  const merged = { ...FIELD_MAP.common, ...sportMap };

  // Reverse: normalizedKey → nestVarName
  const result = new Map<string, string>();
  for (const [nestKey, normalizedKey] of Object.entries(merged)) {
    result.set(normalizedKey, nestKey);
  }
  return result;
}

function formatFieldLabel(fieldId: string): string {
  // camelCase → Title Case: "homeScore" → "Home Score"
  return fieldId
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
